import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { POI_TYPES } from '../../../../../../constants/poiTypes';
import { logError, logInfo } from '../../../../../../utils/logger';
import Sidebar from './Sidebar';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';

// Get API key directly from environment variable
const GOOGLE_MAPS_API_KEY = 'AIzaSyAdNYCigSTxjhN3zL39QmB6H4nL5-zku7A';

// Define libraries array outside component to prevent recreation
const libraries = ['places', 'drawing', 'geometry'];

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: true,
  mapTypeControlOptions: {
    position: 7 // Right top
  },
  styles: [
    {
      featureType: "administrative",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "landscape",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "poi",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "road",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "water",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    }
  ]
};

const MapContainer = ({ shareData, surveyData, pois, routes, files, visibilityState, onVisibilityChange }) => {
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [polylines, setPolylines] = useState([]);
  const mapRef = useRef(null);

  // Initialize map
  const handleMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    logInfo('Map loaded successfully');
  }, []);

  // Update markers when POIs or visibility changes
  useEffect(() => {
    if (!map || !pois?.length) return;

    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    
    // Create new markers
    const newMarkers = [];
    
    pois.forEach(poi => {
      if (!poi.coordinates?.latitude || !poi.coordinates?.longitude) return;
      
      const poiType = poi.type || 'Custom';
      const typeConfig = POI_TYPES[poiType] || POI_TYPES.Custom;
      
      // Check if this POI type is visible
      if (visibilityState?.poiTypes && visibilityState.poiTypes[poiType] === false) {
        return;
      }
      
      // Determine marker size based on zoom level
      const getMarkerSize = () => {
        const zoom = map.getZoom();
        if (zoom <= 7) return visibilityState?.poiSizeFar || 12;
        if (zoom <= 14) return visibilityState?.poiSizeMedium || 16;
        return visibilityState?.poiSizeClose || 24;
      };
      
      // Create marker
      const marker = new window.google.maps.Marker({
        position: {
          lat: Number(poi.coordinates.latitude),
          lng: Number(poi.coordinates.longitude)
        },
        map,
        title: poi.notes || `${typeConfig.prefix || 'POI'}-${poi.id}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: typeConfig.color,
          fillOpacity: 0.8,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
          scale: getMarkerSize() / 4
        }
      });
      
      // Add info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; max-width: 200px;">
            <h3 style="font-weight: bold; margin-bottom: 4px;">${typeConfig.prefix || 'POI'}-${poi.id.slice(-8)}</h3>
            <p style="margin: 0; font-size: 12px;">${poi.notes || ''}</p>
            ${poi.measurements?.length ? 
              `<p style="margin-top: 4px; font-size: 12px;">
                Measurements: ${poi.measurements.map(m => `${m.value}${m.unit}`).join(' × ')}
              </p>` : ''
            }
          </div>
        `
      });
      
      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });
      
      // Update marker size when zoom changes
      map.addListener('zoom_changed', () => {
        marker.setIcon({
          ...marker.getIcon(),
          scale: getMarkerSize() / 8
        });
      });
      
      newMarkers.push(marker);
    });
    
    setMarkers(newMarkers);
    logInfo(`Created ${newMarkers.length} POI markers`);
  }, [map, pois, visibilityState?.poiTypes, visibilityState?.poiSizeFar, visibilityState?.poiSizeMedium, visibilityState?.poiSizeClose]);

  // Update polylines when routes or visibility changes
  useEffect(() => {
    if (!map || !routes?.length) return;

    // Clear existing polylines
    polylines.forEach(polyline => polyline.setMap(null));
    
    // Create new polylines
    const newPolylines = [];
    
    routes.forEach(route => {
      if (!route.trackPoints?.length) return;
      
      // Check if this route is visible
      if (visibilityState?.routes && visibilityState.routes[route.id] === false) {
        return;
      }
      
      // Create path from track points
      const path = route.trackPoints
        .filter(point => point.latitude && point.longitude)
        .map(point => ({
          lat: Number(point.latitude),
          lng: Number(point.longitude)
        }));
      
      if (path.length < 2) return;
      
      // Create polyline
      const polyline = new window.google.maps.Polyline({
        path,
        map,
        strokeColor: route.color || '#3B82F6',
        strokeWeight: visibilityState?.lineWidth || 4,
        strokeOpacity: visibilityState?.lineOpacity || 0.8
      });
      
      newPolylines.push(polyline);
    });
    
    setPolylines(newPolylines);
    logInfo(`Created ${newPolylines.length} route polylines`);
  }, [map, routes, visibilityState?.routes, visibilityState?.lineWidth, visibilityState?.lineOpacity]);

  // Set initial map bounds to include all POIs and routes
  useEffect(() => {
    if (!map || (!pois?.length && !routes?.length)) return;
    
    const bounds = new window.google.maps.LatLngBounds();
    let hasValidPoints = false;
    
    // Add POI points to bounds
    pois?.forEach(poi => {
      if (poi.coordinates?.latitude && poi.coordinates?.longitude) {
        bounds.extend({
          lat: Number(poi.coordinates.latitude),
          lng: Number(poi.coordinates.longitude)
        });
        hasValidPoints = true;
      }
    });
    
    // Add route points to bounds
    routes?.forEach(route => {
      route.trackPoints?.forEach(point => {
        if (point.latitude && point.longitude) {
          bounds.extend({
            lat: Number(point.latitude),
            lng: Number(point.longitude)
          });
          hasValidPoints = true;
        }
      });
    });
    
    if (hasValidPoints) {
      // Add padding to bounds
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const latPadding = (ne.lat() - sw.lat()) * 0.1;
      const lngPadding = (ne.lng() - sw.lng()) * 0.1;
      bounds.extend({ lat: ne.lat() + latPadding, lng: ne.lng() + lngPadding });
      bounds.extend({ lat: sw.lat() - latPadding, lng: sw.lng() - lngPadding });
      
      map.fitBounds(bounds);
    }
  }, [map, pois, routes]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  if (loadError) {
    return (
      <div className="h-full flex items-center justify-center bg-[#2A2B2E]">
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
          Failed to load Google Maps. Please check your internet connection and try again.
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="h-full flex items-center justify-center bg-[#2A2B2E]">
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner />
          <p className="text-white">Loading Google Maps...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <GoogleMap
        ref={mapRef}
        mapContainerStyle={{ width: '100%', height: '100%' }}
        zoom={12}
        center={{ lat: 45.5017, lng: -73.5673 }} // Default center (Montreal)
        options={mapOptions}
        onLoad={handleMapLoad}
      />
      
      <Sidebar
        map={map}
        pois={pois}
        routes={routes}
        files={files}
        onVisibilityChange={onVisibilityChange}
        visibilityState={visibilityState}
      />
      
      {/* Remove API Key display */}
      <style>{`
        .gm-style-cc {
          display: none !important;
        }
        .gmnoprint.gm-style-cc {
          display: none !important;
        }
        .gm-style div[style*="bottom: 0px"] {
          display: none !important;
        }
        div[style*="background-color: rgba(0, 0, 0, 0.5)"] {
          display: none !important;
        }
        div[style*="color: rgb(255, 255, 255)"] {
          display: none !important;
        }
        .gm-style-pbc {
          display: none !important;
        }
      `}</style>
    </div>
  );
};

export default MapContainer;